import React from "react";
import { Clock } from "lucide-react";

/**
 * 24-hour time picker (HH:MM) — independent of browser locale.
 *
 * Renders two native <select> fields so AM/PM never appears, regardless of
 * the user's OS language. Minutes default to a 5-minute grid (matches the
 * APScheduler tick), but any HH:MM string from props.value is accepted.
 */
const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0"));

export default function Time24Picker({ value, onChange, disabled, testId = "time-24" }) {
  const [hh = "07", mm = "30"] = (value || "07:30").split(":");

  // If existing mm is not on the 5-min grid (e.g. user edited via API), still show it.
  const mmOptions = MINUTES.includes(mm) ? MINUTES : [mm, ...MINUTES].sort();

  const update = (newHh, newMm) => onChange(`${newHh}:${newMm}`);

  const selectCls =
    "appearance-none bg-transparent text-sm text-neutral-900 focus:outline-none disabled:opacity-50 pr-1 py-3 font-medium";

  return (
    <div className={`flex items-center gap-1 bg-white border border-brand-line pl-10 pr-3 relative ${disabled ? "opacity-50" : ""}`}>
      <Clock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--brand-green-dark))]" />
      <select
        aria-label="Stunde"
        disabled={disabled}
        value={hh}
        onChange={(e) => update(e.target.value, mm)}
        data-testid={`${testId}-hour`}
        className={selectCls}
      >
        {HOURS.map((h) => (<option key={h} value={h}>{h}</option>))}
      </select>
      <span className="text-neutral-500 select-none">:</span>
      <select
        aria-label="Minute"
        disabled={disabled}
        value={mm}
        onChange={(e) => update(hh, e.target.value)}
        data-testid={`${testId}-minute`}
        className={selectCls}
      >
        {mmOptions.map((m) => (<option key={m} value={m}>{m}</option>))}
      </select>
      <span className="ml-auto text-[10px] uppercase tracking-[0.1em] text-neutral-400">24 h</span>
    </div>
  );
}
